// Training context — loads tours + my progress, runs the active tour overlay across navigation.
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../../lib/api";
import TourOverlay from "./TourOverlay";

const Ctx = createContext(null);
export const useTraining = () => useContext(Ctx) || {};

export const tourMatches = (tour, path) =>
  !tour.route || [tour.route, ...(tour.routes || [])].some((r) => r && matchPath({ path: r, end: true }, path));

export function TrainingProvider({ children }) {
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const load = useCallback(() => api.get("/training/me").then(({ data: d }) => setData(d)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const start = useCallback((key) => {
    const tour = (data?.tours || []).find((t) => t.key === key);
    if (!tour || !tour.steps?.length) return toast.error("This tour has no steps yet");
    if (!tourMatches(tour, window.location.pathname) && tour.route) navigate(tour.route);
    setActive({ tour, idx: 0 });
  }, [data, navigate]);

  const finish = useCallback(async () => {
    const { tour } = active;
    setActive(null);
    try { await api.post(`/training/tours/${tour.key}/complete`); toast.success(`"${tour.title}" completed`); load(); }
    catch { toast.error("Could not save your progress"); }
  }, [active, load]);

  const pageTours = useMemo(() => (data?.tours || []).filter((t) => t.route && t.status !== "draft" && tourMatches(t, pathname)), [data, pathname]);
  const value = { data, load, start, active, pageTours };
  return (
    <Ctx.Provider value={value}>
      {children}
      {active && <TourOverlay tour={active.tour} idx={active.idx} setIdx={(idx) => setActive({ ...active, idx })}
        onClose={() => setActive(null)} onFinish={finish} />}
    </Ctx.Provider>
  );
}
